/* ══════════════════════════════
   SKYFALL — dodge the rising obstacles. Survive 60 seconds!
══════════════════════════════ */

import { setupNavigation, cleanupNavigation } from '../UI/navigation.js';
import { langManager } from '../lang/langManager.js';
import {
  awardMedal,
  renderTitleMedals,
  medalModalHTML,
  getNavH,
  getNavV,
  getGameSoundEnabled,
  setGameSoundEnabled
} from '../utils/utilsJeux.js';
import { log } from '../utils/logging.js';
import {
  initJeuxModal,
  openJeuxModal,
  closeJeuxModal,
  cleanupJeuxModal
} from '../utils/utilsJeux.js';

// ── PAGE ROUTING ───────────────────────────────────────────────
const pageRouteMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  about: '/about',
  games: '/games'
};

const onNavigateHome = () => {
  window.location.href = pageRouteMap.home;
};
window.addEventListener('navigate-home', onNavigateHome);

const onNavigateSection = e => {
  const target = pageRouteMap[e.detail?.section];
  if (target) window.location.href = target;
};
window.addEventListener('navigate-section', onNavigateSection);

setupNavigation();

/* ══════════════════════════════════════════
   SKYFALL — main game logic
══════════════════════════════════════════ */

const t = key => langManager.getText(key);

const canvas = document.getElementById('game');
const ctx = canvas.getContext('2d');
const startBtn = document.getElementById('start-btn');
const msgEl = document.getElementById('skyfall-msg');

// ── Image loading ──────────────────────────────────────────────
const images = {
  roseSky: null,
  player: null,
  bird: null,
  balloon: null,
  montgolfiere: null,
  camion: null
};
let imagesLoaded = 0;
const totalImages = 6;

function loadImage(key, src) {
  const img = new Image();
  img.onload = () => {
    images[key] = img;
    imagesLoaded++;
    if (imagesLoaded === totalImages) drawBackground();
  };
  img.onerror = () => console.error(`Failed to load: ${src}`);
  img.src = src;
}

loadImage('roseSky', '/jeux/skyfall/roseSky.webp');
loadImage('player', '/jeux/skyfall/player.webp');
loadImage('bird', '/jeux/skyfall/bird.webp');
loadImage('balloon', '/jeux/skyfall/balloon.webp');
loadImage('montgolfiere', '/jeux/skyfall/montgolfiere.webp');
loadImage('camion', '/jeux/skyfall/camion-volant.webp');

// ── Sky scroll state ───────────────────────────────────────────
let skyScrollY = 0;
const SKY_ZOOM = 1.5;
const SKY_SCROLL_SPEED = 0.3;

// ── Responsive canvas sizing ───────────────────────────────────
const ASPECT = 2 / 3;

const PROGRESS_BAR_W_MOBILE = 6;
const PROGRESS_BAR_W_DEFAULT = 16;
const PROGRESS_BAR_MARGIN = 4;

let _lastLayoutW = 0;
let _lastLayoutH = 0;

function resizeCanvas() {
  if (document.body.classList.contains('game-playing')) return;

  const layoutW = document.documentElement.clientWidth;
  const layoutH = document.documentElement.clientHeight;

  if (Math.abs(layoutW - _lastLayoutW) < 2 && Math.abs(layoutH - _lastLayoutH) < 2) return;
  _lastLayoutW = layoutW;
  _lastLayoutH = layoutH;

  const isMobile = document.body.classList.contains('is-mobile');
  const isLandscape = window.matchMedia('(orientation: landscape)').matches;
  const overlayActive =
    isMobile && document.getElementById('skyfall-overlay')?.classList.contains('is-active');

  const barW = isMobile && !isLandscape ? PROGRESS_BAR_W_MOBILE : PROGRESS_BAR_W_DEFAULT;
  const totalBarW = barW + PROGRESS_BAR_MARGIN;

  let availW, availH;

  if (overlayActive) {
    const overlayPad = 10 * 2;
    availH = layoutH - overlayPad;
    availW = layoutW - overlayPad - totalBarW;
  } else {
    const msgEl_ = document.getElementById('skyfall-msg');

    const titleEl = document.querySelector('.page-title');
    const hudEl = document.getElementById('hud');
    const btnEl = document.getElementById('start-btn');

    const titleH = titleEl ? titleEl.offsetHeight : 0;
    const hudH = hudEl ? hudEl.offsetHeight : 0;
    const msgH = msgEl_ ? msgEl_.offsetHeight : 0;
    const btnH = btnEl ? btnEl.offsetHeight : 0;

    const mainEl = document.querySelector('main');
    const gameDiv = document.querySelector('.game-div');

    const mainStyle = getComputedStyle(mainEl);
    const mainPadT = parseFloat(mainStyle.paddingTop);
    const mainPadB = parseFloat(mainStyle.paddingBottom);
    const mainPadV = mainPadT + mainPadB;
    const mainPadH = parseFloat(mainStyle.paddingLeft) + parseFloat(mainStyle.paddingRight);

    let gameDivGap = 12,
      nChromeItems = 0;
    if (gameDiv) {
      gameDivGap = parseFloat(getComputedStyle(gameDiv).gap) || 12;
      const children = Array.from(gameDiv.children).filter(
        el => el.offsetParent !== null && el.id !== 'skyfall-overlay'
      );
      nChromeItems = children.length;
    }

    const mainGap = parseFloat(getComputedStyle(mainEl).gap) || 0;
    const navHoriz = getNavH();
    const navVert = getNavV();

    const mainH = mainEl.clientHeight;
    const mainAvailH = mainH - mainPadV;

    const itemsTotalH = titleH + hudH + msgH + btnH;
    const gapsTotalH = gameDivGap * (nChromeItems + 1);
    const chromeItems = itemsTotalH + gapsTotalH + mainGap;

    availH = mainAvailH - chromeItems;
    availW = layoutW - mainPadH - navHoriz - totalBarW;

    console.groupCollapsed(
      '%c[skyfall resize]',
      'color:#9b30d0;font-weight:bold',
      `avail ${Math.round(availW)}×${Math.round(availH)}`
    );
    log('  window              ', layoutW, '×', layoutH);
    log('  h-navbar            ', navVert, '   sidebar-w:', navHoriz);
    log('  main.clientH        ', mainH, '   padT:', mainPadT, 'padB:', mainPadB);
    log('  mainAvailH          ', Math.round(mainAvailH));
    log(
      '  game-div gap        ',
      gameDivGap,
      '× (',
      nChromeItems,
      '+1 items) =',
      Math.round(gapsTotalH)
    );
    log('  title               ', titleH);
    log('  hud                 ', hudH);
    log('  msg                 ', msgH);
    log('  btn                 ', btnH);
    log('  ▶ items+gaps        ', Math.round(chromeItems));
    log('  ▶ avail             ', Math.round(availW), '×', Math.round(availH));
    console.groupEnd();
  }

  let h = Math.max(availH, 200);
  let w = Math.round(h * ASPECT);
  if (w > availW) {
    w = Math.max(availW, 180);
    h = Math.round(w / ASPECT);
  }

  canvas.width = Math.max(w, 180);
  canvas.height = Math.max(h, 260);

  const progressWrap = document.getElementById('skyfall-progress-wrap');
  const timerBar = document.getElementById('skyfall-timer-bar');
  if (progressWrap) {
    progressWrap.style.height = canvas.height + 'px';
    progressWrap.style.width = barW + 'px';
  }
  if (timerBar) {
    timerBar.style.height = canvas.height + 'px';
    timerBar.style.width = barW + 'px';
  }
}

resizeCanvas();
const onSkyfallResize = () => {
  // Save relative position BEFORE canvas is resized
  const relX = player ? player.x / canvas.width : 0.5;
  const relY = player ? player.y / canvas.height : 0.18;

  resizeCanvas();

  if (gameRunning && player) {
    const sf = canvas.width / REFERENCE_WIDTH;
    player.width = 496 * 0.09 * sf;
    player.height = 826 * 0.09 * sf;
    player.maxSpeed = canvas.width * 0.012;
    player.accel = canvas.width * 0.0025;
    player.x = Math.max(
      player.width / 2,
      Math.min(canvas.width - player.width / 2, relX * canvas.width)
    );
    player.y = Math.max(
      player.height / 2,
      Math.min(canvas.height - player.height / 2, relY * canvas.height)
    );

    const W = canvas.width,
      H = canvas.height;
    for (const o of obstacles) {
      const type = OBSTACLE_TYPES.find(t => t.img === o.type);
      if (!type) continue;
      const cx = o.x + o.w / 2;
      const by = o.y + o.h;
      o.w = type.nw * W;
      o.h = type.nh * W;
      o.x = cx - o.w / 2;
      o.y = by - o.h;
      o.speed = H * 0.004 + (elapsed / GOAL_TIME) * H * 0.007 + Math.random() * H * 0.003;
    }
  }

  if (!gameRunning) drawBackground();
};
window.addEventListener('resize', onSkyfallResize);

// ── Sound toggle ───────────────────────────────────────────────
let skyfallSoundEnabled = getGameSoundEnabled();

function initSoundToggle() {
  const btn = document.getElementById('skyfall-sound-toggle');
  if (!btn) return;

  function update() {
    btn.setAttribute('aria-pressed', String(skyfallSoundEnabled));
    btn.classList.toggle('muted', !skyfallSoundEnabled);
    btn.title = skyfallSoundEnabled ? t('soundOff') : t('soundOn');
  }

  btn.addEventListener('click', () => {
    skyfallSoundEnabled = !skyfallSoundEnabled;
    setGameSoundEnabled(skyfallSoundEnabled);
    update();
  });

  update();
}

// ── Sound ──────────────────────────────────────────────────────
const skyfallHitSound = new Audio('/jeux/skyfall/skyfallHit.mp3');
skyfallHitSound.preload = 'auto';
skyfallHitSound.volume = 1;

function playSkyfallHitSound() {
  if (!skyfallSoundEnabled) return;
  try {
    const snd = skyfallHitSound.cloneNode();
    snd.volume = 1;
    const p = snd.play();
    if (p?.catch) p.catch(() => {});
  } catch {
    /* ignore */
  }
}

const skyfallWinSound = new Audio('/jeux/skyfall/skyfallWin.mp3');
skyfallWinSound.preload = 'auto';
skyfallWinSound.volume = 1;

function playSkyfallWinSound() {
  log(
    '[winSound] enabled:',
    skyfallSoundEnabled,
    '| readyState:',
    skyfallWinSound.readyState,
    '| src:',
    skyfallWinSound.src
  );
  if (!skyfallSoundEnabled) return;
  try {
    const snd = skyfallWinSound.cloneNode();
    snd.volume = 1;
    const p = snd.play();
    if (p?.catch) p.catch(() => {});
  } catch {
    /* ignore */
  }
}

// ── Palette ────────────────────────────────────────────────────
const C = {
  brand: '#e73465',
  accent: '#ffa601',
  ok: '#1309a8',
  sky1: '#67a8f3',
  sky2: '#dce3f0',
  ground: '#ffd580',
  white: '#ffffff'
};

// ── Constants ──────────────────────────────────────────────────
const GOAL_TIME = 60;
const INITIAL_LIVES = 3;
const PLAYER_R = 30;
const REFERENCE_WIDTH = 280;

const IDLE_SWAY_AMP = 0.045;
const IDLE_SWAY_FREQ = 0.0006;

const OBSTACLE_TYPES = [
  { img: 'bird', nw: 0.06, nh: 0.058, weight: 5 },
  { img: 'balloon', nw: 0.073, nh: 0.177, weight: 3 },
  { img: 'montgolfiere', nw: 0.15, nh: 0.256, weight: 1 },
  { img: 'camion', nw: 0.179, nh: 0.281, weight: 1 }
];

const TOTAL_WEIGHT = OBSTACLE_TYPES.reduce((s, tp) => s + tp.weight, 0);
function randomObstacleType() {
  let r = Math.random() * TOTAL_WEIGHT;
  for (const tp of OBSTACLE_TYPES) {
    r -= tp.weight;
    if (r <= 0) return tp;
  }
  return OBSTACLE_TYPES[OBSTACLE_TYPES.length - 1];
}

// ── State ──────────────────────────────────────────────────────
let player, obstacles, particles, gameRunning, gameOver, win;
let startTime, elapsed, lives;
let keys = {};
let frameId = null;

// ── Input ──────────────────────────────────────────────────────
const onKeyDown = e => {
  keys[e.key] = true;
  if (['ArrowLeft', 'ArrowRight', ' '].includes(e.key)) e.preventDefault();
};
const onKeyUp = e => {
  keys[e.key] = false;
};
document.addEventListener('keydown', onKeyDown);
document.addEventListener('keyup', onKeyUp);

const btnLeft = document.getElementById('btn-left');
const btnRight = document.getElementById('btn-right');

['pointerdown', 'touchstart'].forEach(ev => {
  btnLeft.addEventListener(
    ev,
    e => {
      if (e.cancelable) e.preventDefault();
      keys['ArrowLeft'] = true;
    },
    { passive: false }
  );
  btnRight.addEventListener(
    ev,
    e => {
      if (e.cancelable) e.preventDefault();
      keys['ArrowRight'] = true;
    },
    { passive: false }
  );
});
['pointerup', 'touchend', 'touchcancel'].forEach(ev => {
  btnLeft.addEventListener(ev, () => {
    keys['ArrowLeft'] = false;
  });
  btnRight.addEventListener(ev, () => {
    keys['ArrowRight'] = false;
  });
});

// ── Start ──────────────────────────────────────────────────────
function setStartBtnState(enabled) {
  startBtn.disabled = !enabled;
  startBtn.textContent = t('start');
}

startBtn.addEventListener('click', () => {
  closeJeuxModal();
  setStartBtnState(false);
  renderTitleMedals('skyfall');
  startGame();
});

function startGame() {
  if (imagesLoaded < totalImages) {
    msgEl.textContent = t('skyfallLoading');
    msgEl.className = 'msg info';
    setStartBtnState(true);
    return;
  }

  startBtn.style.display = '';
  setStartBtnState(false);
  msgEl.style.display = 'none';

  const isMobile = document.body.classList.contains('is-mobile');

  if (isMobile) {
    document.getElementById('skyfall-overlay').classList.add('is-active');
    document.body.classList.add('game-playing');

    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    const barW = isLandscape ? PROGRESS_BAR_W_DEFAULT : PROGRESS_BAR_W_MOBILE;
    const totalBarW = barW + PROGRESS_BAR_MARGIN;
    const pad = 20;
    const availW = window.innerWidth - pad - totalBarW;
    const maxVisibleH = window.innerHeight - pad;
    const fullWidthW = Math.max(availW, 180);
    const fullWidthH = Math.round(fullWidthW / ASPECT);

    let w, h;
    if (fullWidthH <= maxVisibleH) {
      w = fullWidthW;
      h = fullWidthH;
    } else {
      h = Math.max(maxVisibleH, isLandscape ? 220 : 180);
      w = Math.round(h * ASPECT);
      if (w > availW) {
        w = Math.max(availW, 180);
        h = Math.round(w / ASPECT);
      }
    }

    canvas.width = w;
    canvas.height = h;

    const progressWrap = document.getElementById('skyfall-progress-wrap');
    const timerBar = document.getElementById('skyfall-timer-bar');
    if (progressWrap) {
      progressWrap.style.height = canvas.height + 'px';
      progressWrap.style.width = barW + 'px';
    }
    if (timerBar) {
      timerBar.style.height = canvas.height + 'px';
      timerBar.style.width = barW + 'px';
    }

    log('[startGame overlay sizing]', { availW, maxVisibleH, w: canvas.width, h: canvas.height });
  } else {
    _lastLayoutW = 0;
    _lastLayoutH = 0;
    resizeCanvas();
  }

  skyScrollY = 0;

  const sf = canvas.width / REFERENCE_WIDTH;
  player = {
    x: canvas.width / 2,
    y: canvas.height * 0.18,
    speed: 0,
    maxSpeed: canvas.width * 0.012,
    accel: canvas.width * 0.0025,
    friction: 0.82,
    width: 496 * 0.09 * sf,
    height: 826 * 0.09 * sf
  };

  obstacles = [];
  particles = [];
  gameRunning = true;
  gameOver = false;
  win = false;
  startTime = performance.now();
  elapsed = 0;
  lives = INITIAL_LIVES;

  updateProgressBar(0);
  updateHUD();
  if (frameId) cancelAnimationFrame(frameId);
  lastTime = performance.now();
  frameId = requestAnimationFrame(loop);
}

// ── Main loop ──────────────────────────────────────────────────
let lastTime = 0;

function loop(ts) {
  const dt = Math.min((ts - lastTime) / 16.67, 3);
  lastTime = ts;
  if (!gameRunning) return;

  elapsed = (ts - startTime) / 1000;
  updateHUD();
  updateProgressBar(elapsed / GOAL_TIME);

  if (elapsed >= GOAL_TIME) {
    endGame(true);
    return;
  }

  skyScrollY += SKY_SCROLL_SPEED * dt;

  // Sub-step physics + collision when dt is large
  const MAX_STEP = 1;
  let remaining = dt;
  while (remaining > 0) {
    const step = Math.min(remaining, MAX_STEP);
    remaining -= step;
    handleInput(step);
    spawnObstacles(step);
    moveObstacles(step);
    moveParticles(step);
    detectCollisions(step);
    if (!gameRunning) return; // endGame may have been called inside
  }

  draw();
  frameId = requestAnimationFrame(loop);
}

// ── Progress bar ───────────────────────────────────────────────
function updateProgressBar(pct) {
  const fill = document.getElementById('skyfall-timer-fill');
  if (fill) fill.style.height = Math.min(pct * 100, 100) + '%';
}

// ── Input handling ─────────────────────────────────────────────
function handleInput(dt) {
  if (keys['ArrowLeft']) player.speed -= player.accel * dt;
  if (keys['ArrowRight']) player.speed += player.accel * dt;
  player.speed = Math.max(-player.maxSpeed, Math.min(player.maxSpeed, player.speed));
  player.speed *= Math.pow(player.friction, dt);
  player.x += player.speed * dt;
  const halfW = player.width / 2;
  if (player.x < halfW) {
    player.x = halfW;
    player.speed *= -0.4;
  }
  if (player.x > canvas.width - halfW) {
    player.x = canvas.width - halfW;
    player.speed *= -0.4;
  }
}

// ── Obstacle spawning / movement ───────────────────────────────
let spawnAccum = 0;
function spawnObstacles(dt) {
  const rate = 0.03 + (elapsed / GOAL_TIME) * 0.06;
  spawnAccum += rate * dt;
  while (spawnAccum >= 1) {
    spawnAccum -= 1;
    spawnOne();
  }
}

function spawnOne() {
  const W = canvas.width,
    H = canvas.height;
  const type = randomObstacleType();
  const baseSpeed = H * 0.004 + (elapsed / GOAL_TIME) * H * 0.007;
  const w = type.nw * W;
  const h = type.nh * W;
  obstacles.push({
    x: Math.random() * (W - w),
    y: H + h,
    w,
    h,
    speed: baseSpeed + Math.random() * H * 0.003,
    type: type.img
  });
}

function moveObstacles(dt) {
  for (let i = obstacles.length - 1; i >= 0; i--) {
    obstacles[i].y -= obstacles[i].speed * dt;
    if (obstacles[i].y + obstacles[i].h < 0) {
      obstacles.splice(i, 1);
    }
  }
}

// ── Particles ──────────────────────────────────────────────────
function spawnParticles(x, y, color) {
  for (let i = 0; i < 10; i++) {
    const angle = Math.random() * Math.PI * 2;
    const spd = 1.5 + Math.random() * 3;
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * spd,
      vy: Math.sin(angle) * spd,
      life: 1,
      decay: 0.03 + Math.random() * 0.04,
      r: 3 + Math.random() * 4,
      color
    });
  }
}

function moveParticles(dt) {
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.life -= p.decay * dt;
    if (p.life <= 0) particles.splice(i, 1);
  }
}

// ── Collision detection ────────────────────────────────────────

// Replace frame counter with seconds
let invincible = 0; // now in seconds

function pointInTriangle(p, a, b, c) {
  const sign = (p1, p2, p3) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = sign(p, a, b),
    d2 = sign(p, b, c),
    d3 = sign(p, c, a);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

function detectCollisions(step) {
  if (invincible > 0) {
    invincible -= step / 60;
    return;
  }

  const spriteTop = player.y - player.height / 2;
  const spriteBottom = player.y + player.height / 2;
  const spriteLeft = player.x - player.width / 2;
  const spriteRight = player.x + player.width / 2;

  const pTriangle = [
    { x: player.x, y: spriteTop + player.height * 0.25 },
    { x: spriteLeft + player.width * 0.2, y: spriteBottom - player.height * 0.1 },
    { x: spriteRight - player.width * 0.1, y: spriteBottom - player.height * 0.1 }
  ];

  for (const o of obstacles) {
    let hit = false;
    if (o.type === 'balloon' || o.type === 'montgolfiere') {
      const oTriangle = [
        { x: o.x, y: o.y + o.h * 0.1 },
        { x: o.x + o.w, y: o.y + o.h * 0.1 },
        { x: o.x + o.w / 2, y: o.y + o.h }
      ];
      hit =
        pTriangle.some(pt => pointInTriangle(pt, ...oTriangle)) ||
        oTriangle.some(pt => pointInTriangle(pt, ...pTriangle));
    } else {
      const insetX = o.w * 0.07,
        insetY = o.h * 0.07;
      hit = pTriangle.some(
        ({ x, y }) =>
          x > o.x + insetX && x < o.x + o.w - insetX && y > o.y + insetY && y < o.y + o.h - insetY
      );
    }

    if (hit) {
      playSkyfallHitSound();
      spawnParticles(player.x, player.y, C.brand);
      lives--;
      document.getElementById('hud-lives').textContent = '♥'.repeat(lives) || '0';
      invincible = 1.5;
      if (lives <= 0) {
        endGame(false);
        return;
      }
    }
  }
}

// ── HUD ────────────────────────────────────────────────────────
function updateHUD() {
  document.getElementById('hud-time').textContent = Math.floor(elapsed) + 's';
  document.getElementById('hud-lives').textContent = lives > 0 ? '♥'.repeat(lives) : '0';
}

// ── End game ───────────────────────────────────────────────────
function endGame(didWin) {
  log('[endGame] didWin:', didWin); // ← add this

  gameRunning = false;
  win = didWin;
  updateProgressBar(didWin ? 1 : elapsed / GOAL_TIME);

  const timeStr = Math.floor(elapsed) + 's';
  msgEl.style.display = '';
  msgEl.textContent = t('skyfallPrompt');
  msgEl.className = 'msg';
  setStartBtnState(true);

  const earnedMedal =
    didWin && lives === INITIAL_LIVES ? 1 : didWin && lives === 0 ? 3 : didWin ? 2 : null;
  const bestMedal = awardMedal('skyfall', earnedMedal);
  renderTitleMedals('skyfall');

  const showModal = () => {
    document.getElementById('jeux-modal-body').innerHTML = `
      <div class="end-title">${didWin ? '🏆 ' + t('youWin') : t('gameOver')}</div>
      <div class="end-stats">
        <div class="end-stat"><div class="end-stat-val">${timeStr}</div><div class="end-stat-lbl">${t('time')}</div></div>
        <div class="end-stat"><div class="end-stat-val">${lives}</div><div class="end-stat-lbl">${t('skyfallLivesLeft')}</div></div>
      </div>
      ${medalModalHTML(earnedMedal, bestMedal, t)}`;
    openJeuxModal();
  };

  document.getElementById('skyfall-overlay').classList.remove('is-active');
  document.body.classList.remove('game-playing');

  _lastLayoutW = 0;
  _lastLayoutH = 0;
  resizeCanvas();

  startBtn.style.display = '';

  if (didWin) {
    log('[endGame] playing win sound'); // ← add this
    playSkyfallWinSound();

    const groundY = canvas.height - player.height / 2 - 8;
    const startY = player.y;
    const landStart = performance.now();

    const landLoop = ts => {
      const progress = Math.min((ts - landStart) / 800, 1);
      const eased = progress * progress;
      player.y = startY + (groundY - startY) * eased;

      drawBackground();
      drawParticles();
      drawObstacles();
      ctx.fillStyle = C.ground;
      ctx.fillRect(0, canvas.height - 12, canvas.width, 12);
      drawPlayer(ts);

      if (progress < 1) requestAnimationFrame(landLoop);
      else setTimeout(showModal, 500);
    };
    requestAnimationFrame(landLoop);
  } else {
    draw();
    setTimeout(showModal, 600);
  }
}

// ── Drawing ────────────────────────────────────────────────────
function drawBackground() {
  const W = canvas.width;
  const H = canvas.height;

  if (!images.roseSky) {
    const sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, C.sky1);
    sky.addColorStop(1, C.sky2);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, H);
    drawCloud(W * 0.15, H * 0.12, 38);
    drawCloud(W * 0.75, H * 0.22, 28);
    drawCloud(W * 0.45, H * 0.08, 22);
    return;
  }

  const imgAspect = images.roseSky.naturalWidth / images.roseSky.naturalHeight;
  const scaledW = W * SKY_ZOOM;
  const scaledH = scaledW / imgAspect;
  const xOffset = (W - scaledW) / 2;
  const wrapOffset = skyScrollY % scaledH;
  const yOffset = -wrapOffset;

  ctx.drawImage(images.roseSky, xOffset, yOffset, scaledW, scaledH);
  ctx.drawImage(images.roseSky, xOffset, yOffset + scaledH, scaledW, scaledH);
  ctx.drawImage(images.roseSky, xOffset, yOffset - scaledH, scaledW, scaledH);
}

function drawCloud(x, y, r) {
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = '#fff';
  for (const [dx, dy, dr] of [
    [0, 0, r],
    [r * 0.7, -r * 0.2, r * 0.7],
    [-r * 0.65, -r * 0.15, r * 0.65],
    [0, -r * 0.4, r * 0.75]
  ]) {
    ctx.beginPath();
    ctx.arc(x + dx, y + dy, dr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawObstacles() {
  for (const o of obstacles) {
    const img = images[o.type];
    if (img) ctx.drawImage(img, o.x, o.y, o.w, o.h);
    else {
      ctx.fillStyle = C.accent;
      ctx.fillRect(o.x, o.y, o.w, o.h);
    }
  }
}

function drawParticles() {
  for (const p of particles) {
    ctx.globalAlpha = p.life;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fillStyle = p.color;
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawPlayer(ts) {
  const now = ts !== undefined ? ts : performance.now();
  const { x, y, width: w, height: h } = player;
  const blink = invincible > 0 && Math.floor(invincible * 8) % 2 === 0;

  const maxTilt = 0.21;
  const speedTilt = (player.speed / player.maxSpeed) * maxTilt;
  const swayRaw = Math.sin(now * IDLE_SWAY_FREQ * Math.PI * 2) * IDLE_SWAY_AMP;
  const swayWeight = 1 - Math.min(Math.abs(speedTilt) / maxTilt, 1);
  const idleSway = swayRaw * swayWeight;
  const tilt = speedTilt + idleSway;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(tilt);
  if (blink) ctx.globalAlpha = 0.4;
  if (images.player) {
    ctx.drawImage(images.player, -w / 2, -h / 2, w, h);
  } else {
    ctx.beginPath();
    ctx.arc(0, 0, PLAYER_R, 0, Math.PI * 2);
    ctx.fillStyle = C.brand;
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function draw() {
  drawBackground();
  drawParticles();
  drawObstacles();
  drawPlayer(lastTime);
}

if (imagesLoaded === totalImages) drawBackground();
setStartBtnState(true);
initSoundToggle();
renderTitleMedals('skyfall');
initJeuxModal();

/* ── Cleanup on page hide ───────────────────────────────────────*/
window.addEventListener(
  'pagehide',
  () => {
    gameRunning = false;
    if (frameId) cancelAnimationFrame(frameId);
    frameId = null;

    window.removeEventListener('navigate-home', onNavigateHome);
    window.removeEventListener('navigate-section', onNavigateSection);
    window.removeEventListener('resize', onSkyfallResize);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);

    cleanupNavigation();
    cleanupJeuxModal();
  },
  { once: true }
);
